import { useCallback, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { v4 as uuidv4 } from 'uuid'

import useDoubleRequestSubmit from '../../../../../../hooks/formHooks/useDoubleRequestSubmit'

import { createNameFromCustomAudiences } from '../../../../../Multiplatform/LineItemForms/formatters'

import {
  clearCreateLineItem,
  clearCreateLineItemCriterions,
  clearLineItemCriterions,
  createLineItem,
  createLineItemCriterions
} from '../../../../../../modules/actions/lineItems'

import {
  createLineItemCriterionsErrorSelector,
  createLineItemCriterionsWasCreatedSelector,
  lineItemCreatedDataSelector,
  lineItemCreateErrorSelector,
  lineItemWasCreatedSelector
} from '../../../../../../modules/selectors/lineItems'
import { googleRightUploadAdDataSelector, rightSelector } from '../../../../../../modules/selectors/rights'
import { selectedGoogleAdAccountSelector } from '../../../../../../modules/selectors/adAccounts'

import { GOOGLE_PLATFORM } from '../../../../../../constants/selectLists/platformList'
import {
  CPM_BID,
  NAME
} from '../../../../LineItemForms/LineItemGoogleCreate/LineItemGoogleCreateForm/LineItemGoogleCreateMainForm/fields'
import {
  AUDIENCE,
  AUDIENCE_OPTION
} from '../../../../LineItemForms/LineItemGoogleCreate/LineItemGoogleCreateForm/LineItemGoogleAudienceForm/fields'
import { OPTION_YES } from '../../../../../../constants/forms'
import { DIGITAL_ALLOCATION_TYPE_BUDGET } from '../../../../../Multiplatform/Partnerships/RightForms/fields'

export function useLineItemGoogleCreate() {
  const dispatch = useDispatch()

  const [adGroupAudienceSuccess, setAdGroupAudienceSuccess] = useState(false)

  const { id: selectedAdAccountId } = useSelector(selectedGoogleAdAccountSelector)
  const { campaign } = useSelector(googleRightUploadAdDataSelector)
  const campaignId = campaign.id

  const right = useSelector(rightSelector)

  const { allocation_type: allocationTypeOnDigitalRight, bid_cap: rightBidCap } = right

  const isRightAllocationBudget = allocationTypeOnDigitalRight === DIGITAL_ALLOCATION_TYPE_BUDGET

  const createAdGroupHandler = useCallback(
    values => {
      const formattedData = {
        // GA doesn't allow to have the same name for ads, for this we use unique id generation
        [NAME]: `${createNameFromCustomAudiences(values[AUDIENCE])} - ${uuidv4().slice(0, 7)}`,
        // if right allocation is budget, cpm bid is being entered inside form on separate field
        // if allocation is impressions, we take cpm bid from right, or preset it to 0.01
        ...(isRightAllocationBudget ? { [CPM_BID]: values[CPM_BID] } : { [CPM_BID]: rightBidCap || 0.01 })
      }

      dispatch(
        createLineItem(
          {
            ...formattedData,
            account: selectedAdAccountId,
            campaign_id: campaignId,
            status: 'active'
          },
          GOOGLE_PLATFORM
        )
      )
    },
    [dispatch, campaignId, isRightAllocationBudget, rightBidCap, selectedAdAccountId]
  )

  const setAdGroupAudience = useCallback(
    (values, lineItemCreatedData) => {
      if (values[AUDIENCE_OPTION] === OPTION_YES) {
        const { id: assetGroupInternalId } = lineItemCreatedData
        const audienceCriterions = values[AUDIENCE].map(({ value }) => ({
          account: selectedAdAccountId,
          ad_group: assetGroupInternalId,
          user_list: {
            user_list: value
          }
        }))

        dispatch(
          createLineItemCriterions({
            account: selectedAdAccountId,
            operations: audienceCriterions
          })
        )
      } else {
        setAdGroupAudienceSuccess(true)
      }
    },
    [dispatch, selectedAdAccountId]
  )

  const clearSubmitHandler = useCallback(() => {
    // after form submit
    dispatch(clearCreateLineItem())
    dispatch(clearCreateLineItemCriterions())
    dispatch(clearLineItemCriterions())
  }, [dispatch])

  const createAdGroupRequest = useMemo(
    () => ({
      requestHandler: createAdGroupHandler,
      errorSelector: lineItemCreateErrorSelector,
      successSelector: lineItemWasCreatedSelector,
      dataSelector: lineItemCreatedDataSelector
    }),
    [createAdGroupHandler]
  )

  const setAdGroupAudienceRequest = useMemo(
    () => ({
      requestHandler: setAdGroupAudience,
      errorSelector: createLineItemCriterionsErrorSelector,
      successSelector: createLineItemCriterionsWasCreatedSelector,
      manualSuccess: !!adGroupAudienceSuccess
    }),
    [setAdGroupAudience, adGroupAudienceSuccess]
  )

  const requests = useMemo(
    () => [createAdGroupRequest, setAdGroupAudienceRequest],
    [createAdGroupRequest, setAdGroupAudienceRequest]
  )

  return useDoubleRequestSubmit({ requests, clearSubmitHandler, keepFirstRequestProgress: true })
}
